import * as React from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import { CommandItem } from '@heidi/design-system/command';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

import type { PatientProfileSearchResult } from '../../../contracts';
import { patientResultRowMessages } from './patient-result-row.messages';

interface PatientResultRowProps {
  result: PatientProfileSearchResult;
  onSelect: (value: string) => void;
  compactPrimaryText?: boolean;
}

/**
 * A single patient row in the find-or-link dropdown.
 *
 * Highlight = "what Enter will link". Cmdk's `data-selected` drives the
 * styling, so it tracks pre-highlight, auto-link candidate, hover, and
 * arrow-nav uniformly. The selected row uses the design-system "pressed"
 * surface — bg shift alone is enough; no border or accent bar.
 */
export const PatientResultRow: React.FC<PatientResultRowProps> = ({
  result,
  onSelect,
  compactPrimaryText = false,
}) => {
  const intl = useIntl();

  const fullName =
    [result.first_name, result.last_name].filter(Boolean).join(' ') ||
    result.demographic_string ||
    intl.formatMessage(patientResultRowMessages.unnamedPatient);

  const secondaryLine = getSecondaryLine(result, intl);
  return (
    <CommandItem
      value={result.id}
      onSelect={onSelect}
      data-testid="patient-result-row"
      className={cn(
        'flex flex-col items-start gap-y-0.5 px-3 py-2.5',
        'data-[selected=true]:bg-surface-pressed'
      )}
    >
      {compactPrimaryText ? (
        <TypographyCaption className="truncate text-text-primary">
          {fullName}
        </TypographyCaption>
      ) : (
        <TypographyP2Bold className="truncate text-text-primary">
          {fullName}
        </TypographyP2Bold>
      )}
      {secondaryLine && (
        <TypographyCaption className="truncate text-text-secondary">
          {secondaryLine}
        </TypographyCaption>
      )}
    </CommandItem>
  );
};

/**
 * Pick the most useful disambiguator for the row's secondary line.
 * Priority:
 *   1. AI-generated gist of the most recent session ("Last visit: ...")
 *   2. Relative time of the most recent session ("Saw last week")
 *   3. DOB ("03/03/1994") for a brand-new patient with no prior session
 */
function getSecondaryLine(
  result: PatientProfileSearchResult,
  intl: IntlShape
): string | null {
  const gist = result.last_session_gist?.trim();
  if (gist) {
    return intl.formatMessage(patientResultRowMessages.lastVisit, { gist });
  }

  if (result.last_seen_at) {
    const relative = formatRelative(result.last_seen_at, intl.locale);
    if (relative) {
      return intl.formatMessage(patientResultRowMessages.sawRelative, { relative });
    }
  }

  if (result.birth_date) {
    return formatDob(result.birth_date, intl);
  }

  return null;
}

function formatDob(iso: string, intl: IntlShape): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.valueOf())) return null;
  return intl.formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatRelative(iso: string, locale: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.valueOf())) return null;
  const diffMs = Date.now() - date.valueOf();
  if (diffMs < 0) return null;

  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(diffMs / day);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (days === 0) return rtf.format(0, 'day');
  if (days < 7) return rtf.format(-days, 'day'); // "yesterday" / "2 days ago"
  if (days < 14) return rtf.format(-1, 'week'); // "last week"
  if (days < 30) return rtf.format(-Math.floor(days / 7), 'week');
  if (days < 60) return rtf.format(-1, 'month'); // "last month"
  if (days < 365) return rtf.format(-Math.floor(days / 30), 'month');
  return rtf.format(-Math.floor(days / 365), 'year');
}
